import { Box, IconButton, InputAdornment, TextField, Typography } from '@mui/material'
import { Check, ContentCopy } from '@mui/icons-material'
import makeStyles from '@mui/styles/makeStyles'
import React, { useState } from 'react'
import { useHistory } from 'react-router-dom'
import { isSuccessWithData, useFirebase } from 'src/firebase'
import { Program } from 'src/legacy/models/firebase'
import { getModule, HOME_VIEW_ENROLLMENT_STATES } from 'src/participantsHome/sortParticipants'
import {
  GetDevPanelPatientQuery,
  useFindDevPanelPatientByEmailLazyQuery,
  useGetDevPanelPatientLazyQuery,
  useGetDevPanelPatientQuery,
} from './devPanelQueries.generated'

type Patient = GetDevPanelPatientQuery['patients'][number]

// Same grouping as the home list; states the list never shows (e.g. ended or declined) fall back to the raw state.
const formatModule = (patient: Patient, programs: Record<string, Program> | undefined) => {
  const isHomeViewState = HOME_VIEW_ENROLLMENT_STATES.some(
    ({ enrollmentState }) => enrollmentState === patient.enrollment.state
  )
  if (!isHomeViewState) {
    return patient.enrollment.state
  }
  const { moduleLabel, themeLabel } = getModule(patient, programs)
  return `${moduleLabel} · ${themeLabel}`
}

const useStyles = makeStyles((theme) => ({
  panel: {
    flexShrink: 0,
    marginTop: 'auto',
    padding: theme.spacing(1, 2),
    borderTop: `1px solid ${theme.palette.divider}`,
  },
  value: {
    minWidth: 0,
    fontFamily: 'monospace',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
}))

const CopyRow = ({ value }: { value: string }) => {
  const classes = useStyles()
  const [copied, setCopied] = useState(false)

  const copy = async () => {
    await navigator.clipboard.writeText(value)
    setCopied(true)
    setTimeout(() => setCopied(false), 1500)
  }

  return (
    <Box display="flex" alignItems="center" gap={0.5}>
      <Typography variant="caption" className={classes.value} title={value}>
        {value}
      </Typography>
      <IconButton size="small" onClick={copy} aria-label={`copy ${value}`}>
        {copied ? <Check fontSize="inherit" color="success" /> : <ContentCopy fontSize="inherit" />}
      </IconButton>
    </Box>
  )
}

// Opens a participant from a pasted (or typed and Enter-submitted) uid or email. The check mark shows
// once the panel has loaded the participant it opened.
const OpenParticipantField = ({ loadedUid }: { loadedUid: string | undefined }) => {
  const history = useHistory()
  const [value, setValue] = useState('')
  const [error, setError] = useState<string>()
  const [openedUid, setOpenedUid] = useState<string>()
  const [findByEmail, { loading: findingByEmail }] = useFindDevPanelPatientByEmailLazyQuery({
    fetchPolicy: 'network-only',
  })
  // Same query the panel runs, so an opened participant is already cached.
  const [findByUid, { loading: findingByUid }] = useGetDevPanelPatientLazyQuery()

  const openParticipant = (uid: string) => {
    setOpenedUid(uid)
    history.push(`/participant/${uid}`)
  }

  const open = async (input: string) => {
    const query = input.trim()
    setError(undefined)
    setOpenedUid(undefined)
    if (!query) {
      return
    }
    // An unmatched uid is never opened: the panel would have no participant to show and unmount this field.
    if (!query.includes('@')) {
      const { data, error: lookupError } = await findByUid({ variables: { uid: query } })
      if (lookupError) {
        setError(lookupError.message)
      } else if (data?.patients[0]) {
        openParticipant(query)
      } else {
        setError('No participant found with that uid')
      }
      return
    }
    const { data, error: searchError } = await findByEmail({ variables: { email: query } })
    if (searchError) {
      setError(searchError.message)
      return
    }
    const match = data?.searchPatientsV2.patients.find(
      (patient) => patient.email?.toLowerCase() === query.toLowerCase()
    )
    if (match) {
      openParticipant(match.uid)
    } else {
      setError('No participant found with that email')
    }
  }

  return (
    <Box
      component="form"
      onSubmit={async (event: React.FormEvent) => {
        event.preventDefault()
        await open(value)
      }}
    >
      <TextField
        size="small"
        fullWidth
        sx={{ mt: 1 }}
        placeholder="Paste uid or email to open"
        value={value}
        disabled={findingByEmail || findingByUid}
        error={Boolean(error)}
        helperText={error}
        InputProps={{
          endAdornment: openedUid !== undefined && openedUid === loadedUid && (
            <InputAdornment position="end">
              <Check fontSize="small" color="success" />
            </InputAdornment>
          ),
        }}
        onChange={(event) => {
          setValue(event.target.value)
          setOpenedUid(undefined)
        }}
        onPaste={async (event) => {
          event.preventDefault()
          const pasted = event.clipboardData.getData('text')
          setValue(pasted)
          await open(pasted)
        }}
      />
    </Box>
  )
}

// Local-only developer aid, docked at the bottom of the participant list.
const DevPanel = ({ uid }: { uid: string }) => {
  const classes = useStyles()
  const { data, previousData } = useGetDevPanelPatientQuery({ variables: { uid } })
  const programsRequest = useFirebase<Record<string, Program>>('programs')
  // While the next participant loads, the previous one keeps the panel's height but stays hidden,
  // so the list doesn't jump and their uid can't be copied by mistake.
  const isLoadingNext = !data
  const patient = (data ?? previousData)?.patients[0]

  if (!patient) {
    return null
  }

  const source = typeof patient.details?.source === 'string' ? patient.details.source : '—'

  return (
    <Box className={classes.panel}>
      <Box visibility={isLoadingNext ? 'hidden' : undefined}>
        <Typography variant="subtitle1">
          {formatModule(patient, isSuccessWithData(programsRequest) ? programsRequest.data : undefined)}
        </Typography>
        <Typography variant="caption" color="textSecondary" display="block">
          {`${patient.enrollment.programID} · ${patient.language ?? 'en'} · ${source}`}
        </Typography>
        <CopyRow value={patient.uid} />
        {patient.email && <CopyRow value={patient.email} />}
      </Box>
      <OpenParticipantField loadedUid={isLoadingNext ? undefined : patient.uid} />
    </Box>
  )
}

export default DevPanel
